import { Injectable, Injector } from '@angular/core';
import { UIRouter, HookMatchCriteria } from '@uirouter/angular';
import { firstValueFrom, take } from 'rxjs';
import { isPlanningSmartPlanningBranch, STATE_NAMES, uiRouterStateMatches } from 'app/common/state-names';
import { CompanyService } from 'app/original-services/company.service';
import { environment } from 'environments/environment';
import { createMobileMaxWidthMediaQuery, matchesMobileMaxWidthViewport } from 'app/common/viewport-breakpoints';
import { FeatureFlagsService } from './feature-flags.service';
import { FINANCING_COUNTRIES } from 'app/services/contracts/financing-access.util';

const AI_FORECAST_HEALTH_FLAG = 'ai_forecast_health';

/**
 * Transition sources that can only originate from the user: `sref` is a uiSref click (and the menu
 * handlers, which pass it explicitly via {@link RoutingService.go}), `url` is the address bar or the
 * browser's Back/Forward buttons. Everything else — notably UI-Router's `unknown` default — is a
 * programmatic navigation issued by app code.
 */
const USER_INITIATED_SOURCES = ['sref', 'url'];

export function isInsightsHealthTab(stateName: string | undefined, stateParam: unknown): boolean {
  return (
    uiRouterStateMatches(stateName, STATE_NAMES.insightsAIForecastTab) ||
    (uiRouterStateMatches(stateName, STATE_NAMES.insights) &&
      (stateParam === 0 || stateParam === '0' || stateParam == null))
  );
}

function isSmartPlanningRoute(stateName: string | undefined): boolean {
  return isPlanningSmartPlanningBranch(stateName);
}

function isAiForecastHealthGuardTarget(stateName: string | undefined): boolean {
  return (
    uiRouterStateMatches(stateName, STATE_NAMES.insightsAIForecastTab) ||
    uiRouterStateMatches(stateName, STATE_NAMES.insights) ||
    isSmartPlanningRoute(stateName)
  );
}

/**
 * Registers UI-Router transition guards related to feature flags.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagRouteGuardService {
  constructor(
    private router: UIRouter,
    private injector: Injector,
  ) {
    const featureFlags = this.injector.get(FeatureFlagsService);
    const routingService = this.router.stateService;

    // UI Router logs every transition rejection as an error by default, including the
    // benign control-flow rejections (SUPERSEDED=2, ABORTED=3, IGNORED=5) that occur
    // normally — e.g. when a route re-navigates to itself for URL-param sync before the
    // previous transition has settled. Swallow those and keep logging real failures
    // (INVALID=4 / ERROR=6).
    const BENIGN_REJECTION_TYPES = [2, 3, 5];
    routingService.defaultErrorHandler((error: any) => {
      if (error && BENIGN_REJECTION_TYPES.indexOf(error.type) !== -1) {
        return;
      }
      console.error(error);
    });

    // --- Company-switch re-entry guard ---
    // A company switch fires a sanctioned reload:true re-navigation (from header.goToFirstPage)
    // so the target page re-instantiates for the new company. But the *outgoing* page, still
    // mounted, reacts to the company change and fires its own reload:false self-navigation
    // (e.g. insights syncing its tab), which would supersede and abort the reload:true one —
    // leaving the page (and the shared onboarding-layout spinner) stuck on the previous company.
    //
    // While a switch is in progress, abort the *programmatic* reload:false transitions so only the
    // sanctioned reload:true re-entry survives. Because UI Router runs BEFORE hooks before a
    // transition is marked "current" (transition.run -> startTransition), aborting here prevents the
    // stale navigation from ever superseding the in-flight reload:true one. ABORTED(3) is benign and
    // swallowed by the handler above.
    //
    // A user click must NEVER be dropped here. This guard used to abort every reload:false
    // transition, which silently swallowed the user's own sidebar clicks for as long as the window
    // stayed open — and because UI Router only restores the URL of an aborted transition while it is
    // still the latest one (stateService `isLatest` check), the address bar could move while the
    // <ui-view> kept the previous page. Treat a user-initiated navigation as an intentional override
    // of the switch's landing page: close the window and let it through.
    const companyService = this.injector.get(CompanyService);
    this.router.transitionService.onBefore({}, (trans: any) => {
      if (!companyService.switchInProgress) {
        return true;
      }
      const options = trans?.options?.() ?? {};
      if (options.reload) {
        return true;
      }
      if (USER_INITIATED_SOURCES.indexOf(options.source) !== -1) {
        companyService.endCompanySwitch();
        return true;
      }
      if (!environment.production) {
        // This class of failure was previously invisible: the rejection is swallowed above and
        // RoutingService.go discards the promise. Leave a breadcrumb.
        console.warn('[company-switch] aborted stale navigation to', trans?.to?.()?.name);
      }
      return false;
    });
    this.router.transitionService.onSuccess({}, () => {
      if (companyService.switchInProgress) {
        companyService.endCompanySwitch();
      }
    });
    this.router.transitionService.onError({}, (trans: any) => {
      if (!companyService.switchInProgress) {
        return;
      }
      // Close the window on a genuine failure of any transition, and on ANY failure of the
      // sanctioned re-entry itself — a reload:true navigation that is aborted or superseded (e.g. by
      // a redirect hook that drops `reload`) would otherwise leave the window stuck open until the
      // safety timeout, silently dropping every click in between. Benign rejections of the stale
      // navigation we just aborted must still leave the window open.
      const type = trans?.error?.()?.type;
      const isSanctionedReentry = !!trans?.options?.()?.reload;
      if (isSanctionedReentry || BENIGN_REJECTION_TYPES.indexOf(type) === -1) {
        companyService.endCompanySwitch();
      }
    });

    const aiForecastHealthCriteria: HookMatchCriteria = {
      to: (state: any) => isAiForecastHealthGuardTarget(state?.name ?? state),
    } as any;

    this.router.transitionService.onBefore(aiForecastHealthCriteria, (trans: any) => {
      // On mobile, MobileInsightsOnlyGuardService owns "which state to be on" — it always lands on
      // Health regardless of this flag (insights.component.ts shows a "not available" message there
      // instead of the dashboard when the flag is off), so don't fight that with our own
      // redirect-away-from-Health here.
      if (matchesMobileMaxWidthViewport(createMobileMaxWidthMediaQuery())) {
        return true;
      }
      // Preserve the incoming transition's `reload` through the redirect below. Without this,
      // `stateService.target()` sets reloadState=undefined and the redirect strips a reload:true
      // (company-switch) navigation's app-shell reload on prod. See plan "Fix B".
      const reload = trans?.options?.()?.reload;
      try {
        const toName = trans?.to()?.name;
        const stateParam = trans?.params?.()?.state;
        const toHealth = isInsightsHealthTab(toName, stateParam);
        const toSmartPlanning = isSmartPlanningRoute(toName);
        if (!toHealth && !toSmartPlanning) {
          return true;
        }
        return new Promise(async (resolve) => {
          try {
            await featureFlags.waitForFeatureFlags?.(7000);
          } catch (_) {
            // ignore
          }
          const redirect = () => {
            if (toSmartPlanning) {
              resolve(routingService.target(STATE_NAMES.planningCompany, {}, { reload }));
            } else {
              resolve(routingService.target(STATE_NAMES.insightsOutlookTab, { state: 1 }, { reload }));
            }
          };
          try {
            if (!featureFlags.isFeatureEnabled?.(AI_FORECAST_HEALTH_FLAG)) {
              redirect();
              return;
            }
          } catch (_) {
            redirect();
            return;
          }
          resolve(true);
        });
      } catch (_) {
        return isSmartPlanningRoute(trans?.to()?.name)
          ? routingService.target(STATE_NAMES.planningCompany, {}, { reload })
          : routingService.target(STATE_NAMES.insightsOutlookTab, { state: 1 }, { reload });
      }
    });

    // Financing is available for DE and AT companies only. Block direct navigation for other
    // countries (country codes are stored lowercase), redirecting to the Contracts tab.
    const financingCriteria: HookMatchCriteria = { to: STATE_NAMES.financing } as any;
    this.router.transitionService.onBefore(financingCriteria, () => {
      try {
        return new Promise(async (resolve) => {
          try {
            const companyService = this.injector.get(CompanyService);
            const company: any = await firstValueFrom(companyService.getCurrentCompany().pipe(take(1)));
            if (!FINANCING_COUNTRIES.includes(company?.country)) {
              resolve(routingService.target(STATE_NAMES.Contracts));
              return;
            }
          } catch (_) {
            resolve(routingService.target(STATE_NAMES.Contracts));
            return;
          }
          resolve(true);
        });
      } catch (_) {
        return routingService.target(STATE_NAMES.Contracts);
      }
    });
  }
}
